import classnames from "classnames";
import React from "react";
import { useEffect, useRef, useState } from "react";
import { Node } from "slate";
import { findDOMNode } from "slate-react";
import { useClickOutsideHook } from "../../../../../../packages/ui/src/utils/clickOutsideHook";
import { getWindowClientRect, keepRectWithinBounds, ResponsiveBreakpoints, translate } from "../../../../../../packages/ui/src/utils/uiHelpers";
import { Portal, useWindowDimensions } from "../../../../core/atoms";

type TooltipOrigin = "nodeLeft" | "window";
type PositionCSSProperties = Pick<React.CSSProperties, "display" | "position" | "top" | "left">;

interface Props {
  node: Node;
  children?: React.ReactNode;
  className?: string;
  origin?: TooltipOrigin;
  onClose?: () => void | Promise<void>;
}

const offscreenPosition: PositionCSSProperties = {
  display: "none",
  position: "absolute",
  top: -1000,
  left: -1000,
};

/**
 * Fullscreen when editing on mobile, otherwise position next to link
 */
const getTooltipPosition = (node: Node, origin: TooltipOrigin): PositionCSSProperties => {
  const { width } = useWindowDimensions();

  if (!node) {
    return offscreenPosition;
  }

  switch (origin) {
    case "window":
      return {
        position: "fixed",
        left: 0,
        top: 0,
      };
    case "nodeLeft":
      // Position menu just below the document node
      const nodeRect = findDOMNode(node).getBoundingClientRect();

      // TODO: hardcoding dimensions as having trouble getting ref to hoverMenu
      const menuWidth = width < ResponsiveBreakpoints.tablet ? 300 : 335;
      const menuRect = translate({ width: menuWidth, height: 45 }, nodeRect.left, nodeRect.bottom);

      // Shifts menuRect so it's still onscreen if the node is too close to the edge.
      // NB. top + left are relative to the current viewport, need to add scroll offsets
      const { top, left } = keepRectWithinBounds(menuRect, getWindowClientRect());

      return {
        position: "absolute",
        top: top + window.pageYOffset,
        left: left + window.pageXOffset,
      };
    default:
      return offscreenPosition;
  }
};

/**
 * A container for a menu that should hover by the provided node.
 */
export const HoverMenu = (props: Props) => {

  // Edge case: menu might be "open" during first render, BEFORE props.node gets mounted to DOM.
  const [node, setNode] = useState<Node>(null);
  useEffect(() => { setNode(props.node); }, [props.node]);

  const divRef = useRef<HTMLDivElement>();
  useClickOutsideHook(divRef, props.onClose);

  const position = getTooltipPosition(node, props.origin);

  return (
    <Portal>
      <div ref={divRef} style={position} className={classnames(props.className)}>
        {props.children}
      </div>
    </Portal>
  );
};
